import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { ICON_SHAPES, type IconName, type IconShape } from './icon-shapes';

export type { IconName } from './icon-shapes';

/**
 * Joins path data into one `d`. A path that starts with a relative `m` is
 * measured from the origin; after another subpath it would be measured from
 * that subpath's end, so it is anchored with `M0 0` (a lone moveto is never
 * stroked).
 */
export function joinSubpaths(paths: readonly string[]): string {
  return paths.map((d) => (d.startsWith('m') ? `M0 0${d}` : d)).join('');
}

/**
 * Inline lucide SVG icon (lucide is ISC-licensed). Each icon is one stroked
 * `<path>` plus one filled `<path>`, with no control flow in the template, so
 * hydration has nothing to record per icon. Color follows currentColor (use
 * Tailwind text-* on the host); size via the `size` input or h-/w- utilities
 * on the host.
 */
@Component({
  selector: 'padspin-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex shrink-0' },
  template: `
    <svg
      [attr.width]="size()"
      [attr.height]="size()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path [attr.d]="paths().stroked" />
      <path [attr.d]="paths().filled" fill="currentColor" />
    </svg>
  `,
})
export class IconComponent {
  readonly name = input.required<IconName>();
  readonly size = input(24);

  protected readonly paths = computed(() => {
    const shapes: readonly IconShape[] = ICON_SHAPES[this.name()];
    const filled = shapes.flatMap((shape) =>
      typeof shape === 'string' ? [] : [shape.filled],
    );
    return {
      stroked: joinSubpaths(
        shapes.filter((shape): shape is string => typeof shape === 'string'),
      ),
      filled: filled.length > 0 ? joinSubpaths(filled) : null,
    };
  });
}
